import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgxUiLoaderModule, NgxUiLoaderService } from 'ngx-ui-loader';
import { AuthService } from '../../../../core/services/auth.service';
import { VerifyOtpDto } from '../../../../core/interfaces/auth.interface';
import { SwalService } from '../../../../core/services/swal.service';
import { ModalDataService } from '../../../../core/services/modal-data.service';
import { ModalService } from '../../../../core/services/modal.service';

@Component({
  selector: 'app-verify-otp',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxUiLoaderModule],
  templateUrl: './verify-otp.component.html',
  styleUrls: ['./verify-otp.component.scss'],
})
export class VerifyOtpComponent implements OnInit {
  @Input() phoneNumber: string = '';
  @Input() indicative: string = '';
  @Output() otpVerified = new EventEmitter<any>();
  @Output() otpFailed = new EventEmitter<string>();

  otpForm: FormGroup;
  loading = false;
  submitted = false;
  remainingTime = 60;
  canResend = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private swalService: SwalService,
    private modalService: ModalService
  ) {
    this.otpForm = this.fb.group({
      otpCode: [
        '',
        [Validators.required, Validators.minLength(4), Validators.maxLength(6)],
      ],
    });
  }

  ngOnInit(): void {
    console.log('VerifyOtpComponent - phoneNumber:', this.phoneNumber);
    console.log('VerifyOtpComponent - indicative:', this.indicative);

    // Récupérer les données depuis le service statique
    const modalData = ModalDataService.getData();
    if (modalData) {
      this.phoneNumber = modalData.phoneNumber || this.phoneNumber;
      this.indicative = modalData.indicative || this.indicative;
      console.log('Data loaded from ModalDataService:', modalData);
      console.log('Updated phoneNumber:', this.phoneNumber);
      console.log('Updated indicative:', this.indicative);
    }

    this.startCountdown();
  }

  onSubmit() {
    this.submitted = true;

    if (this.otpForm.invalid) {
      return;
    }

    this.loading = true;
    this.ngxUiLoaderService.startLoader('verify-otp-submit-loader');

    const verifyData: VerifyOtpDto = {
      code: this.otpForm.get('otpCode')?.value,
      phoneNumber: this.phoneNumber,
      indicative: this.indicative,
    };

    console.log('Submitting OTP with data:', verifyData);

    this.authService.verifyOtp(verifyData).subscribe({
      next: (response) => {
        this.loading = false;
        this.ngxUiLoaderService.stopLoader('verify-otp-submit-loader');

        // Afficher un message de succès avec Swal
        this.swalService.success(
          'Code OTP validé !',
          'Votre numéro de téléphone a été vérifié avec succès.'
        );

        // Émettre l'événement avec l'otpId et les données
        const otpData = {
          otpId: response.otpId,
          phoneNumber: this.phoneNumber,
          indicative: this.indicative,
          message: response.message,
        };

        this.otpVerified.emit(otpData);

        // Émettre un événement via le service modal avec vérification de sécurité
        try {
          if (this.modalService && this.modalService.emitEvent) {
            this.modalService.emitEvent({
              type: 'otp_verified',
              data: otpData,
            });
          }
        } catch (error) {
          console.warn('ModalService not available:', error);
        }

        // Fermer le modal après un délai
        setTimeout(() => {
          try {
            if (this.modalService && this.modalService.closeAll) {
              this.modalService.closeAll();
            }
          } catch (error) {
            console.warn('ModalService not available for closing:', error);
          }
        }, 2000);
      },
      error: (error) => {
        this.loading = false;
        this.ngxUiLoaderService.stopLoader('verify-otp-submit-loader');

        const errorMessage =
          error.error?.message || 'Code OTP invalide. Veuillez réessayer.';

        // Afficher l'erreur avec Swal
        this.swalService.error('Erreur de validation', errorMessage);

        this.otpFailed.emit(errorMessage);
      },
    });
  }

  resendOtp() {
    if (!this.canResend) return;

    this.loading = true;
    this.ngxUiLoaderService.startLoader('verify-otp-resend-loader');

    const requestData = {
      phoneNumber: this.phoneNumber,
      indicative: this.indicative,
    };

    console.log('Resending OTP with data:', requestData);

    this.authService.requestOtp(requestData).subscribe({
      next: () => {
        this.loading = false;
        this.ngxUiLoaderService.stopLoader('verify-otp-resend-loader');
        this.startCountdown();

        // Afficher un message de succès avec Swal
        this.swalService.success(
          'Code OTP renvoyé !',
          'Un nouveau code de vérification a été envoyé à votre numéro.'
        );
      },
      error: (error) => {
        this.loading = false;
        this.ngxUiLoaderService.stopLoader('verify-otp-resend-loader');

        const errorMessage =
          error.error?.message || "Erreur lors de l'envoi du code OTP.";

        // Afficher l'erreur avec Swal
        this.swalService.error("Erreur d'envoi", errorMessage);
      },
    });
  }

  private startCountdown() {
    this.remainingTime = 60;
    this.canResend = false;

    const timer = setInterval(() => {
      this.remainingTime--;
      if (this.remainingTime <= 0) {
        clearInterval(timer);
        this.canResend = true;
      }
    }, 1000);
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
